'use client'

import { ComponentType, FC, PropsWithChildren } from 'react'
import { useGetTranslations } from '@/root/shared-hooks'
import { BookingContainer } from '@/root/components/tuuleliinid'
import { AppLayout, BookTicket } from '@/root/modules/tuuleliinid'
import { Banner } from '@/root/ui/tuuleliinid/Banner/Banner'
import { Loader } from '@/root/ui/shared'
import { useConfirmPage } from '@/root/modules/shared/ConfirmPage'
import Link from 'next/link'
import { Reservation } from '@/root/shared-types'
import type { MontonioPaymentMethod } from '@/root/shared-types/reservation/reservation-types'

export interface TicketPageProps {
  reservation: Reservation
  privatePersonForm?: boolean
  paymentMethods: MontonioPaymentMethod[]
  /** The page frame. The TL header and footer by default; the trial theme passes its unbranded one. */
  Layout?: ComponentType<PropsWithChildren>
}

export const ConfirmTicketsPage: FC<TicketPageProps> = ({
  reservation: staticReservation,
  privatePersonForm,
  paymentMethods,
  Layout = AppLayout,
}) => {
  const { reservation, error, isLoading, isOffer, errorBody, sailPackages } = useConfirmPage(staticReservation)
  const { getTranslation } = useGetTranslations()

  if (error) {
    return (
      <Layout>
        <h1>Error page</h1>
      </Layout>
    )
  }
  if (isLoading) {
    return (
      <Layout>
        <BookingContainer>
          <div className="flex justify-center py-40">
            <Loader />
          </div>
        </BookingContainer>
      </Layout>
    )
  }

  if (isOffer) {
    return (
      <Layout>
        <BookingContainer>
          {errorBody && (
            <Banner title={getTranslation('ticketBannerErrorTitle')} variant="error" className="my-6 border-0">
              <p className="font-normal whitespace-pre-line">{errorBody}</p>
            </Banner>
          )}
          {reservation && (
            <BookTicket
              ticketData={reservation}
              privatePersonForm={privatePersonForm}
              paymentMethods={paymentMethods}
              sailPackages={sailPackages}
            />
          )}
        </BookingContainer>
      </Layout>
    )
  } else {
    return (
      <Layout>
        <BookingContainer>
          <div className="flex flex-col items-center justify-center gap-4 py-20">
            <p className="text-lg">{getTranslation('confirmPageReservationStatus', { status: reservation?.status })}</p>
            <Link href="/" className="text-primary-base underline">
              {getTranslation('ticketReservationNotFoundButton')}
            </Link>
          </div>
        </BookingContainer>
      </Layout>
    )
  }
}
